// Editable Creative Brief panel for Step 4 (Review & Generate).
//
// Shows the active campaign's creativeBrief so operators can sanity-
// check + adjust the intent that will be fed to the Director on this
// run. Inline edits stamp Campaign.creativeBrief via PATCH; refresh
// re-derives from the campaign's targeting + creatives.
//
// Read-mode is compact (badges + 1-line previews) so it doesn't dwarf
// the Generate button. Edit mode expands to textareas + chip editors
// for the most-edited fields. Audience.segments / geo / interests are
// shown as read-only badges since they reflect platform targeting, not
// operator intent.

import { useEffect, useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Button, Badge, Wrap, WrapItem,
  Textarea, Input, IconButton, Spinner, Collapse, useToast, Divider
} from '@chakra-ui/react';
import { apiJson, apiFetch } from '../../auth/apiFetch';

type CreativeBrief = {
  goal?:         string;
  pitch?:        string;
  focus?:        string;
  cta_emphasis?: string;
  tone?:         string[];
  audience?: {
    description?: string;
    segments?:    string[];
    geo?:         string[];
    ageRange?:    string;
    interests?:   string[];
  };
  evidence?: {
    adCount?:     number;
    hasInsights?: boolean;
  };
  derivedFrom?:  'ingest' | 'manual';
  model?:        string;
  promptVersion?: string;
};

type Props = {
  campaignId: string;
};

// Age of the brief, in words, plus whole days for the staleness threshold.
//
// WHY THIS EXISTS: the confirm screen showed "Goal: Drive purchases of the
// Merino Crewneck Sweater" with a matching pitch DATED TWO DAYS EARLIER, while
// the products list on the same screen correctly showed a different garment —
// and the only hint was a bare `toLocaleDateString()`, which reads as
// provenance metadata, not as a warning. The Generate button sits directly
// below. Measured in the live app 2026-08-27.
//
// WHY THE FIX IS A LABEL AND NOT AUTO-REBINDING — this was checked before
// changing anything, because two prior decisions make rebinding actively wrong:
//   • b6ba434 shipped this panel as a HAND-EDITABLE brief with an explicit
//     Refresh, "so operators can sanity-check + adjust the intent that will
//     feed the Director on THIS run". Silently re-deriving on every product
//     change would discard typed work (`derivedFrom: 'manual'`) and fire a paid
//     LLM re-derive on every picker tick.
//   • 5a82ad3 named this exact hazard in its own message — "Same shape of
//     mistake as the campaign creative brief being fed to every product's
//     Director round: campaign-scoped data applied to a product-scoped job" —
//     then fixed the campaign PINS and deliberately left the brief
//     campaign-scoped. The scoping is a known, accepted design; what was
//     missing was the operator being able to SEE it.
// There is also no field to bind against: `CreativeBrief` carries no productId
// or productTitle, so a precise product-mismatch check is not available. Age
// and scope are, and both are already in hand.
export function briefAge(iso: string): { label: string; days: number } | null {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const sec = Math.round((Date.now() - t) / 1000);
  // A clock-skewed future timestamp must not render as "-3d ago".
  if (sec < 0) return { label: 'just now', days: 0 };
  if (sec < 60) return { label: 'just now', days: 0 };
  const min = Math.round(sec / 60);
  if (min < 60) return { label: `${min}m ago`, days: 0 };
  const hr = Math.round(min / 60);
  if (hr < 24) return { label: `${hr}h ago`, days: 0 };
  const days = Math.floor(hr / 24);
  return { label: `${days}d ago`, days };
}

// A brief older than this is called out rather than merely dated. One day is
// deliberately conservative: the observed failure was two days old, and a brief
// derived in this session is not what an operator needs warning about.
export const BRIEF_STALE_DAYS = 1;

export function WizardBriefEditor({ campaignId }: Props) {
  const toast = useToast();
  const [brief, setBrief]               = useState<CreativeBrief | null>(null);
  const [briefDerivedAt, setBriefDerivedAt] = useState<string | null>(null);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [saving, setSaving]             = useState(false);
  const [isEditing, setIsEditing]       = useState(false);
  const [draft, setDraft]               = useState<CreativeBrief>({});
  const [expanded, setExpanded]         = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await apiJson<{ campaign?: { creativeBrief?: CreativeBrief | null; briefDerivedAt?: string | null } }>(
        `/api/campaigns/${encodeURIComponent(campaignId)}`
      );
      const c = res.campaign;
      setBrief(c?.creativeBrief ?? null);
      setBriefDerivedAt(c?.briefDerivedAt ?? null);
    } catch {
      setBrief(null);
      setBriefDerivedAt(null);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { void load(); }, [load]);

  const startEdit = () => {
    setDraft(brief ? structuredClone(brief) : {});
    setIsEditing(true);
    setExpanded(true);
  };
  const cancelEdit = () => {
    setDraft({});
    setIsEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/campaigns/${encodeURIComponent(campaignId)}/brief`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ brief: draft })
      });
      if (!res.ok) throw new Error(await res.text());
      toast({ title: 'Brief saved', status: 'success', duration: 3000 });
      setIsEditing(false);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e instanceof Error ? e.message : String(e), status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiJson<{ ok?: boolean; skipped?: boolean; reason?: string }>(
        `/api/campaigns/${encodeURIComponent(campaignId)}/derive-brief?force=true`,
        { method: 'POST' }
      );
      if (r.skipped) {
        toast({ title: 'Skipped', description: r.reason || 'no change', status: 'info', duration: 4000 });
      } else {
        toast({ title: 'Brief re-derived', status: 'success', duration: 3000 });
      }
      await load();
    } catch (e) {
      toast({ title: 'Refresh failed', description: e instanceof Error ? e.message : String(e), status: 'error', duration: 5000 });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" px={4} py={3}>
        <HStack><Spinner size="xs" /><Text fontSize="sm" color="brand.muted">Loading creative brief…</Text></HStack>
      </Box>
    );
  }

  const hasBrief = !!brief && Object.keys(brief).length > 0;
  const derivedAt = briefDerivedAt ? new Date(briefDerivedAt) : null;
  const age = briefDerivedAt ? briefAge(briefDerivedAt) : null;
  const isStale = !!age && age.days >= BRIEF_STALE_DAYS;

  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" px={4} py={3} bg="white">
      <HStack justify="space-between" align="flex-start" wrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
            Creative Brief
          </Text>
          <Badge variant="subtle" colorScheme="purple" fontSize="9px" px={1.5} py={0.5}>AI inferred</Badge>
          {brief?.derivedFrom === 'manual' && (
            <Badge variant="subtle" colorScheme="green" fontSize="9px" px={1.5} py={0.5}>Edited</Badge>
          )}
          {/* SCOPE, stated on the panel itself. This brief belongs to the
              campaign, not to the products chosen below — the single most
              load-bearing thing an operator needs to know before pressing
              Generate, and it was nowhere on screen. */}
          <Badge variant="subtle" colorScheme="gray" fontSize="9px" px={1.5} py={0.5}>Campaign-wide</Badge>
          {/* AGE, not a calendar date. `toLocaleDateString()` reads as
              provenance; "2d ago" in orange reads as a question to answer. */}
          {age && (
            <Badge
              variant="subtle"
              colorScheme={isStale ? 'orange' : 'gray'}
              fontSize="9px"
              px={1.5}
              py={0.5}
              title={derivedAt ? `Derived ${derivedAt.toLocaleString()}` : undefined}
            >
              {age.label}
            </Badge>
          )}
        </HStack>
        <HStack spacing={2}>
          {!isEditing && (
            <>
              <Button size="xs" variant="outline" onClick={refresh} isLoading={refreshing}>
                {hasBrief ? 'Refresh' : 'Derive now'}
              </Button>
              {hasBrief && (
                <Button size="xs" variant="brand" onClick={startEdit}>
                  Edit
                </Button>
              )}
            </>
          )}
          {isEditing && (
            <>
              <Button size="xs" variant="ghost" onClick={cancelEdit} isDisabled={saving}>Cancel</Button>
              <Button size="xs" variant="brand" onClick={save} isLoading={saving}>Save</Button>
            </>
          )}
          {hasBrief && !isEditing && (
            <IconButton
              aria-label="Toggle details"
              size="xs"
              variant="ghost"
              onClick={() => setExpanded(v => !v)}
              icon={<Box as="span" transform={expanded ? 'rotate(180deg)' : undefined} transition="transform 120ms">▾</Box>}
            />
          )}
        </HStack>
      </HStack>

      {!hasBrief && !isEditing && (
        <Text fontSize="sm" color="brand.muted" mt={2}>
          No brief derived yet. Click "Derive now" to extract one from this campaign's creatives + targeting.
        </Text>
      )}

      {/* STALE-BRIEF CALLOUT. Same orange treatment Step 4 already uses for
          "Account setup incomplete" (Step4Generate.tsx), so it reads as the
          same class of "resolve this before generating" notice rather than a
          new invented surface.

          Deliberately NON-BLOCKING: a campaign-wide brief that is two days old
          is often perfectly correct, and this panel's own Refresh / Edit
          buttons are the remedy, two inches above. Blocking Generate on brief
          age would be a false gate. The risk being surfaced is real though —
          per 5a82ad3 the campaign brief IS fed to every product's Director
          round, and the video prompt states "All ad copy is composited
          downstream", so a stale goal steers TITLES and copy even when the
          camera prompt names the right product. */}
      {hasBrief && !isEditing && isStale && (
        <VStack
          align="stretch"
          spacing={1}
          mt={2}
          bg="orange.50"
          borderWidth="1px"
          borderColor="orange.300"
          borderRadius="md"
          p={3}
        >
          <Text fontSize="11px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="0.06em">
            Brief may be stale — derived {age?.label}
          </Text>
          <Text fontSize="sm" color="brand.ink">
            This brief belongs to the campaign, not to the products selected below, and it has not
            been re-derived since it was written. Check that the goal and pitch still describe
            what you are about to generate.
          </Text>
          <Text fontSize="11px" color="brand.muted">
            • Refresh re-derives it from the campaign's current targeting and creatives · Edit sets
            it by hand · either one clears this notice.
          </Text>
        </VStack>
      )}

      {hasBrief && !isEditing && (
        <>
          {/* Scope restated in prose next to the goal, because the goal is the
              field that actually misled — it named a garment that was not in
              the selection. */}
          {!isStale && (
            <Text fontSize="10px" color="brand.muted" mt={2}>
              Campaign-wide brief — not bound to the products selected below.
            </Text>
          )}
          <HStack mt={2} spacing={1.5} flexWrap="wrap">
            {brief?.focus && <Badge colorScheme="purple" variant="subtle" fontSize="11px" textTransform="capitalize">{brief.focus.replace(/_/g, ' ')}</Badge>}
            {brief?.cta_emphasis && <Badge colorScheme="green" variant="subtle" fontSize="11px" textTransform="capitalize">CTA: {brief.cta_emphasis.replace(/_/g, ' ')}</Badge>}
            {Array.isArray(brief?.tone) && brief.tone.slice(0, 3).map((t, i) => (
              <Badge key={i} colorScheme="orange" variant="subtle" fontSize="11px">{t}</Badge>
            ))}
          </HStack>
          {brief?.goal && (
            <Text fontSize="sm" color="brand.ink" mt={2} noOfLines={expanded ? undefined : 1}>
              <Text as="span" color="brand.muted" fontWeight="700" mr={2}>Goal:</Text>{brief.goal}
            </Text>
          )}
          <Collapse in={expanded} animateOpacity>
            <VStack align="stretch" spacing={3} mt={3}>
              {brief?.pitch && (
                <Box>
                  <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" mb={1}>Pitch</Text>
                  <Text fontSize="sm" color="brand.ink" fontStyle="italic">{brief.pitch}</Text>
                </Box>
              )}
              {brief?.audience?.description && (
                <Box>
                  <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" mb={1}>Audience</Text>
                  <Text fontSize="sm" color="brand.ink">{brief.audience.description}</Text>
                  <HStack mt={1.5} spacing={1.5} flexWrap="wrap">
                    {brief.audience.ageRange && <Badge variant="outline" fontSize="10px">Age {brief.audience.ageRange}</Badge>}
                    {(brief.audience.geo || []).slice(0, 4).map((g, i) => (
                      <Badge key={i} variant="outline" fontSize="10px">{g}</Badge>
                    ))}
                    {(brief.audience.segments || []).slice(0, 4).map((s, i) => (
                      <Badge key={i} variant="subtle" colorScheme="blue" fontSize="10px">{s}</Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </VStack>
          </Collapse>
        </>
      )}

      {isEditing && (
        <VStack align="stretch" spacing={3} mt={3}>
          <Field label="Goal">
            <Textarea
              size="sm"
              value={draft.goal || ''}
              onChange={(e) => setDraft({ ...draft, goal: e.target.value })}
              placeholder="What is this campaign trying to do?"
              rows={2}
            />
          </Field>
          <Field label="Pitch">
            <Textarea
              size="sm"
              value={draft.pitch || ''}
              onChange={(e) => setDraft({ ...draft, pitch: e.target.value })}
              placeholder="The argument the campaign makes to the audience."
              rows={3}
            />
          </Field>
          <HStack spacing={3} align="flex-start">
            <Field label="Focus" flex={1}>
              <Input
                size="sm"
                value={draft.focus || ''}
                onChange={(e) => setDraft({ ...draft, focus: e.target.value })}
                placeholder="price | scarcity | lifestyle | social_proof | …"
              />
            </Field>
            <Field label="CTA emphasis" flex={1}>
              <Input
                size="sm"
                value={draft.cta_emphasis || ''}
                onChange={(e) => setDraft({ ...draft, cta_emphasis: e.target.value })}
                placeholder="urgent | aspirational | low_friction | …"
              />
            </Field>
          </HStack>
          <Field label="Tone">
            <ChipEditor
              items={draft.tone || []}
              placeholder="add a tone word"
              onAdd={v => setDraft({ ...draft, tone: [...(draft.tone || []), v] })}
              onRemove={v => setDraft({ ...draft, tone: (draft.tone || []).filter(x => x !== v) })}
            />
          </Field>
          <Divider />
          <Text fontSize="11px" fontWeight="700" color="brand.muted" textTransform="uppercase" letterSpacing="0.06em">Audience</Text>
          <Field label="Description">
            <Textarea
              size="sm"
              value={draft.audience?.description || ''}
              onChange={(e) => setDraft({ ...draft, audience: { ...(draft.audience || {}), description: e.target.value } })}
              placeholder="Portrait of who this campaign is for."
              rows={2}
            />
          </Field>
          <HStack spacing={3} align="flex-start">
            <Field label="Age range" flex={1}>
              <Input
                size="sm"
                value={draft.audience?.ageRange || ''}
                onChange={(e) => setDraft({ ...draft, audience: { ...(draft.audience || {}), ageRange: e.target.value } })}
                placeholder="25–45"
              />
            </Field>
          </HStack>
          <Field label="Segments">
            <ChipEditor
              items={draft.audience?.segments || []}
              placeholder="add a segment"
              onAdd={v => setDraft({ ...draft, audience: { ...(draft.audience || {}), segments: [...(draft.audience?.segments || []), v] } })}
              onRemove={v => setDraft({ ...draft, audience: { ...(draft.audience || {}), segments: (draft.audience?.segments || []).filter(x => x !== v) } })}
            />
          </Field>
        </VStack>
      )}
    </Box>
  );
}

function Field({ label, children, flex }: { label: string; children: React.ReactNode; flex?: number }) {
  return (
    <Box flex={flex}>
      <Text fontSize="xs" color="brand.muted" mb={1}>{label}</Text>
      {children}
    </Box>
  );
}

function ChipEditor({
  items, placeholder, onAdd, onRemove
}: {
  items: string[];
  placeholder: string;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
}) {
  const [input, setInput] = useState('');
  const commit = () => {
    const v = input.trim();
    if (!v) return;
    onAdd(v);
    setInput('');
  };
  return (
    <VStack align="stretch" spacing={2}>
      <Wrap spacing={2}>
        {items.map((item, i) => (
          <WrapItem key={i}>
            <Badge
              variant="subtle"
              colorScheme="purple"
              fontSize="11px"
              px={2}
              py={1}
              borderRadius="md"
              textTransform="none"
              fontWeight="600"
              display="inline-flex"
              alignItems="center"
              gap={1}
            >
              {item}
              <IconButton
                aria-label={`Remove ${item}`}
                size="xs"
                variant="ghost"
                minW="14px"
                h="14px"
                onClick={() => onRemove(item)}
                icon={<Box as="span" fontSize="10px" lineHeight={1}>×</Box>}
              />
            </Badge>
          </WrapItem>
        ))}
      </Wrap>
      <HStack spacing={2}>
        <Input
          size="sm"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === ',')     { e.preventDefault(); commit(); }
          }}
          placeholder={placeholder}
        />
        <Button size="sm" variant="outline" onClick={commit} isDisabled={!input.trim()}>Add</Button>
      </HStack>
    </VStack>
  );
}
